import React, { useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Card,
  CardBody,
  CardHeader,
  Badge,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { auditLogActions, auditLogTypes } from "./utils";

/**
 * Props:
 * @param {Object} dataDetail
 * @param {boolean} isOpenModal
 * @param {Function} toggleModal
 * @param {Object} usersMap
 */
const DetailAuditLogs = ({
  dataDetail,
  isOpenModal,
  toggleModal,
  usersMap = {},
}) => {
  const { t } = useTranslation(["auditLogs"]);

  const trackingKeyLabel = useMemo(() => {
    const typeValue = dataDetail?.type || dataDetail?.entity_type || "";
    if (typeValue.includes("transport_locality")) {
      return "Tracking Key";
    }
    return t("table.customer_code");
  }, [dataDetail?.type, dataDetail?.entity_type, t]);

  const userInfo = useMemo(() => {
    if (!dataDetail?.user_code) return null;
    return usersMap[dataDetail.user_code] || null;
  }, [dataDetail?.user_code, usersMap]);

  const getActionLabel = actionValue => {
    const action = auditLogActions.find(item => item.key === actionValue);
    if (action) {
      return action.label;
    }
    return actionValue || "-";
  };

  const getTypeLabel = typeValue => {
    const raw = typeValue || dataDetail?.type || dataDetail?.entity_type || "";
    if (!raw) return "-";

    let type = auditLogTypes.find(item => item.key === raw);

    if (!type && raw) {
      type = auditLogTypes.find(
        item => item.key.toUpperCase() === raw.toUpperCase()
      );
    }

    if (!type && raw) {
      const upperRaw = raw.toUpperCase();
      type = auditLogTypes.find(item => {
        const upperKey = item.key.toUpperCase();
        return (
          upperRaw === upperKey ||
          upperRaw.includes(upperKey) ||
          upperKey.includes(upperRaw)
        );
      });
    }

    return type ? type.label : raw;
  };

  const formatDate = dateString => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatTimestamp = timestamp => {
    if (!timestamp) return "-";
    try {
      const timestampMs =
        typeof timestamp === "string"
          ? parseInt(timestamp) * 1000
          : timestamp * 1000;
      const date = new Date(timestampMs);
      return date.toLocaleString("vi-VN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (e) {
      return timestamp;
    }
  };

  const formatJsonDisplay = jsonString => {
    if (!jsonString) return null;
    try {
      const parsed =
        typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  const getDataByAction = () => {
    const action = dataDetail?.action;
    const value = dataDetail?.value;

    if (!value) return { type: null, data: null };

    switch (action) {
      case "VIEW":
        return { type: "view", data: null };

      case "CREATE":
        return { type: "create", data: value.after };

      case "UPDATE":
        return {
          type: "update",
          data: {
            before: value.before,
            after: value.after,
          },
        };

      case "DELETE":
        return { type: "delete", data: value.after };

      default:
        return {
          type: "update",
          data: {
            before: value.before,
            after: value.after,
          },
        };
    }
  };

  const { type: displayType, data: displayData } = getDataByAction();

  const shouldShowDataSection = displayType !== "view" && displayData;

  const getDataSectionTitle = () => {
    switch (displayType) {
      case "create":
        return t("label.created_data");
      case "delete":
        return t("label.deleted_data");
      case "update":
      default:
        return t("label.changes");
    }
  };

  const getDataSectionIcon = () => {
    switch (displayType) {
      case "create":
        return "fas fa-plus-circle";
      case "delete":
        return "fas fa-trash-alt";
      case "update":
      default:
        return "fas fa-exchange-alt";
    }
  };

  return (
    <Modal isOpen={isOpenModal} toggle={toggleModal} size="lg" centered>
      <ModalHeader toggle={toggleModal}>{t("title.detail")}</ModalHeader>
      <ModalBody className="p-3 pt-2">
        <Card className="mb-2">
          <CardBody className="p-3">
            <Row className="row-cols-1 row-cols-md-2 ">
              <Col>
                <FormGroup className="mb-2">
                  <Label className="mb-1">{trackingKeyLabel}</Label>
                  <input
                    type="text"
                    className="form-control bg-light border-1"
                    readOnly
                    value={dataDetail?.tracking_key || "-"}
                    style={{ cursor: "default" }}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-2">
                  <Label className="mb-1">{t("table.user_name")}</Label>
                  <input
                    type="text"
                    className="form-control bg-light border-1"
                    readOnly
                    value={userInfo?.display_name || userInfo?.username || "-"}
                    style={{ cursor: "default" }}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row className="row-cols-1 row-cols-md-2 ">
              <Col>
                <FormGroup className="mb-2">
                  <Label className="mb-1">{t("table.user_email")}</Label>
                  <input
                    type="text"
                    className="form-control bg-light border-1"
                    readOnly
                    value={userInfo?.email || "-"}
                    style={{ cursor: "default" }}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-2">
                  <Label className="mb-1">{t("table.action")}</Label>
                  <div
                    className="form-control d-flex align-items-center bg-light border-1"
                    style={{ minHeight: "38px" }}
                  >
                    {getActionLabel(dataDetail?.action)}
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <Row className="row-cols-1 row-cols-md-2 ">
              <Col>
                <FormGroup className="mb-0">
                  <Label className="mb-1">{t("table.type")}</Label>
                  <input
                    type="text"
                    className="form-control bg-light border-1"
                    readOnly
                    value={getTypeLabel(dataDetail?.type)}
                    style={{ cursor: "default" }}
                  />
                </FormGroup>
              </Col>
              <Col>
                <FormGroup className="mb-0">
                  <Label className="mb-1">{t("table.created_at")}</Label>
                  <input
                    type="text"
                    className="form-control bg-light border-1"
                    readOnly
                    value={formatTimestamp(dataDetail?.maker_date)}
                    style={{ cursor: "default" }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {shouldShowDataSection && (
          <Card className="mb-0 shadow-sm">
            <CardHeader
              className="text-white fw-semibold d-flex align-items-center"
              style={{
                backgroundColor:
                  displayType === "create"
                    ? "#28a745"
                    : displayType === "delete"
                    ? "#dc3545"
                    : "#556EE6",
                fontSize: "15px",
                padding: "0.75rem 1rem",
              }}
            >
              <i
                className={getDataSectionIcon()}
                style={{
                  marginRight: "10px",
                  fontSize: "16px",
                  width: "20px",
                  textAlign: "center",
                }}
              ></i>
              {getDataSectionTitle()}
            </CardHeader>

            <CardBody className="p-3">
              {displayType === "update" && (
                <>
                  {displayData.before && (
                    <FormGroup className="mb-3">
                      <div className="d-flex align-items-center mb-2">
                        <i
                          className="fas fa-minus-circle text-danger"
                          style={{
                            marginRight: "8px",
                            fontSize: "16px",
                            width: "20px",
                            textAlign: "center",
                          }}
                        ></i>
                        <Label
                          className="fw-bold mb-0"
                          style={{ fontSize: "14px" }}
                        >
                          {t("label.old_values")}
                        </Label>
                      </div>
                      <pre
                        className="p-2 rounded border mb-0"
                        style={{
                          maxHeight: "220px",
                          overflow: "auto",
                          fontSize: "12px",
                          backgroundColor: "#fff5f5",
                          borderColor: "#ffcdd2",
                          color: "#c62828",
                          lineHeight: "1.5",
                        }}
                      >
                        {formatJsonDisplay(displayData.before)}
                      </pre>
                    </FormGroup>
                  )}

                  {displayData.after && (
                    <FormGroup className="mb-0">
                      <div className="d-flex align-items-center mb-2">
                        <i
                          className="fas fa-plus-circle text-success"
                          style={{
                            marginRight: "8px",
                            fontSize: "16px",
                            width: "20px",
                            textAlign: "center",
                          }}
                        ></i>
                        <Label
                          className="fw-bold mb-0"
                          style={{ fontSize: "14px" }}
                        >
                          {t("label.new_values")}
                        </Label>
                      </div>
                      <pre
                        className="p-2 rounded border mb-0"
                        style={{
                          maxHeight: "220px",
                          overflow: "auto",
                          fontSize: "12px",
                          backgroundColor: "#f1f8f4",
                          borderColor: "#c8e6c9",
                          color: "#2e7d32",
                          lineHeight: "1.5",
                        }}
                      >
                        {formatJsonDisplay(displayData.after)}
                      </pre>
                    </FormGroup>
                  )}
                </>
              )}

              {displayType === "create" && (
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <i
                      className="fas fa-plus-circle text-success"
                      style={{
                        marginRight: "8px",
                        fontSize: "16px",
                        width: "20px",
                        textAlign: "center",
                      }}
                    ></i>
                    <Label
                      className="fw-bold mb-0"
                      style={{ fontSize: "14px" }}
                    >
                      {t("label.created_values")}
                    </Label>
                  </div>
                  <pre
                    className="p-2 rounded border mb-0"
                    style={{
                      maxHeight: "220px",
                      overflow: "auto",
                      fontSize: "12px",
                      backgroundColor: "#f1f8f4",
                      borderColor: "#c8e6c9",
                      color: "#2e7d32",
                      lineHeight: "1.5",
                    }}
                  >
                    {formatJsonDisplay(displayData)}
                  </pre>
                </div>
              )}

              {displayType === "delete" && (
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <i
                      className="fas fa-trash-alt text-danger"
                      style={{
                        marginRight: "8px",
                        fontSize: "16px",
                        width: "20px",
                        textAlign: "center",
                      }}
                    ></i>
                    <Label
                      className="fw-bold mb-0"
                      style={{ fontSize: "14px" }}
                    >
                      {t("label.deleted_values")}
                    </Label>
                  </div>
                  <pre
                    className="p-2 rounded border mb-0"
                    style={{
                      maxHeight: "220px",
                      overflow: "auto",
                      fontSize: "12px",
                      backgroundColor: "#fff5f5",
                      borderColor: "#ffcdd2",
                      color: "#c62828",
                      lineHeight: "1.5",
                    }}
                  >
                    {formatJsonDisplay(displayData)}
                  </pre>
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </ModalBody>
      <ModalFooter style={{ padding: "0.75rem 1rem" }}>
        <Button color="secondary" onClick={toggleModal}>
          {t("button.close")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DetailAuditLogs;
